<template>
    <v-container 
        id="offline-clipboard"
        :pa-0="this.$vuetify.breakpoint.xs"
    >
        <v-row v-if="this.$vuetify.breakpoint.xs">
            <!-- Mobile Company Name Header -->
            <v-col>
                <div class="mobile-company-header pa-3">
                    {{ companyName }}
                </div>
                <v-divider class="mt-2" />
            </v-col>
        </v-row>
        <v-row
            :no-gutters="this.$vuetify.breakpoint.xs"
        >
            <!-- Page Header -->
            <v-col
                :class="this.$vuetify.breakpoint.xs ? 'px-3' : ''"
            >
                <v-toolbar flat>
                    <span class="toolbar-title">Clipboard</span>
                    <v-chip
                        class="ml-2 text-caption"
                        small
                        outlined
                        color="#122448"
                    >
                        {{ tickets.length }}
                    </v-chip>
                    <v-spacer />
                    <ClipboardItemPositionControls 
                        v-if="this.$vuetify.breakpoint.smAndUp"                       
                        :tickets="tickets"
                        :selected="selected"
                        @move-clipboard-items="updateClipboardItems"
                    />
                    <v-btn
                        v-if="this.$vuetify.breakpoint.smAndUp"
                        class="ma-1 action-btn text-subtitle-2"
                        depressed
                        color="primary"
                        :disabled="respondDisabled"
                        @click="respond"
                    >
                        <v-icon
                            left
                            class="action-icon"
                        >
                            reply
                        </v-icon>
                        Respond
                    </v-btn>
                </v-toolbar>
            </v-col>
        </v-row>
        <v-row 
            class="mt-0"
            :no-gutters="this.$vuetify.breakpoint.xs"
        >
            <v-col v-if="this.$vuetify.breakpoint.smAndUp">
                <v-data-table
                    v-if="tickets.length"
                    id="offline-clipboard-table"
                    v-model="selected"
                    fixed-header
                    :headers="headers"
                    :items="tickets"
                    item-key="TR_rowid"
                    show-select
                    :item-class="itemRowColor"
                    hide-default-footer
                    :items-per-page="itemsPerPage"
                >
                    <template
                        v-slot:[`header.data-table-select`]="{ on, props }"
                    >
                        <v-simple-checkbox
                            v-ripple
                            v-bind="props"
                            on-icon="check_box"
                            indeterminate-icon="indeterminate_check_box"
                            off-icon="check_box_outline_blank"
                            v-on="on"
                        />
                    </template>
                    <template v-slot:[`header.clipboard`]>
                        <v-checkbox
                            on-icon="bookmark"
                            off-icon="bookmark"
                            disabled
                        />
                    </template>
                    <template
                        v-slot:[`item.data-table-select`]="{ isSelected, select }"
                    >
                        <v-simple-checkbox
                            v-ripple
                            :value="isSelected"
                            on-icon="check_box"
                            off-icon="check_box_outline_blank"
                            @input="select($event)"
                        />
                    </template>
                    <template v-slot:[`item.clipboard`]>
                        <v-checkbox
                            on-icon="bookmark"
                            off-icon="bookmark"
                            disabled
                        />
                    </template>
                    <template v-slot:[`item.TR_stage`]="{item}">
                        <v-chip
                            x-small
                            :color="statusChipColor(item.TR_stage)"
                            outlined
                            :class="statusChipClass(item.TR_stage)"
                        >
                            {{ statusText(item.TR_stage) }}
                        </v-chip>
                    </template>
                    <template v-slot:[`item.ticketRevision`]="{item}">
                        <router-link
                            :to="'/offline/tickets/details?tid='+encodeURIComponent(guidNormal(item.tid))"
                            style="text-decoration: none; color: #1976D2"
                        >
                            {{ item.ticketRevision }}
                        </router-link>
                    </template>
                    <template v-slot:[`item.priority`]="{item}">
                        <v-icon 
                            small
                            :color="priorityIconColor(item.priority)"
                        >
                            {{ priorityIcon(item.priority) }}
                        </v-icon> {{ item.priority }}
                    </template>
                    <template v-slot:[`item.TR_insight_risk_score`]="{item}">
                        {{  riskScoreText(item.TR_insight_risk_score) }}
                    </template>
                    <template v-slot:[`item.TR_locator`]="{item}">
                        {{ item.TR_locator_account || 'Un-Assigned' }}
                    </template>
                    <template v-slot:[`item.expires_date`]="{item}">
                        {{ formatTicketDate(item.expires_date) }}
                    </template>
                    <template v-slot:[`item.TR_seq_date`]="{item}">
                        {{ formatTicketDate(item.TR_seq_date) }}
                    </template>
                    <template v-slot:[`item.work_date`]="{item}">
                        {{ formatTicketDate(item.work_date) }}
                    </template>
                    <template v-slot:[`item.duedate`]="{item}">
                        {{ formatTicketDate(item.duedate) }}
                    </template>
                    <template v-slot:[`item.location`]="{item}">
                        {{ truncateField(item.location) }}
                    </template>
                    <template v-slot:[`item.TS_updated`]="{item}">
                        {{ formatTicketDate(item.TS_updated) }}
                    </template>
                    <template v-slot:[`item.TR_response_date`]="{item}">
                        {{ formatTicketDate(item.TR_response_date) }}
                    </template>
                    <template v-slot:[`item.TR_updated`]="{item}">
                        {{ formatTicketDate(item.TR_updated) }}
                    </template>
                </v-data-table>
            </v-col>
            <v-col
                v-else
                class="pb-14"
            >
                <v-divider class="mx-4" />
                <v-data-iterator
                    id="ticket-cards"
                    :items="tickets"
                    item-key="TR_rowid"
                    fixed-header
                    show-select
                    loading-text="Loading items..."
                    no-data-text=""
                    hide-default-footer
                    :items-per-page="itemsPerPage"
                    @toggle-select-all="mobileSelectAll"
                >
                    <template v-slot:header>
                        <v-toolbar
                            v-if="!!tickets.length"
                            id="tickets-mobile-data-toolbar"
                            flat
                            class="px-3"
                        >
                            <div class="checkbox-container">
                                <v-checkbox 
                                    v-model="mobileSelectAll"
                                    on-icon="check_box"
                                    off-icon="check_box_outline_blank"
                                />
                                <v-checkbox
                                    on-icon="bookmark"
                                    off-icon="bookmark"
                                    disabled
                                />
                            </div>
                            <v-spacer />
                            <ClipboardItemPositionControls 
                                :tickets="tickets"
                                :selected="selected"
                                @move-clipboard-items="updateClipboardItems"
                            />
                        </v-toolbar>
                    </template>
                    <template v-slot:default="props">
                        <TicketsMobileCard
                            v-for="item in props.items"
                            :key="item.TR_rowid"
                            :item="item"
                            :item-left-border-color="itemRowColor(item)"
                            :status-text="statusText(item.TR_stage)"
                            :status-chip-color="statusChipColor(item.TR_stage)"
                            :status-chip-class="statusChipClass(item.TR_stage)"
                            :due-date-text="dueDateText(item.duedate)"
                            :priority-icon="priorityIcon(item.priority)"
                            :priority-icon-color="priorityIconColor(item.priority)"
                            :is-selected="!!selected.find(ticket => guidEqual(ticket.TR_rowid, item.TR_rowid))"
                        >
                            <template v-slot:selectCheckbox>
                                <v-checkbox 
                                    v-model="selected"
                                    on-icon="check_box"
                                    off-icon="check_box_outline_blank"
                                    :value="item"
                                    :multiple="true"
                                    :value-comparator="setMobileCheckBoxState"
                                />
                            </template>
                            <template v-slot:clipboardCheckbox>
                                <v-checkbox
                                    on-icon="bookmark"
                                    off-icon="bookmark"
                                    disabled
                                />
                            </template>
                        </TicketsMobileCard>
                    </template>
                </v-data-iterator>
                <v-divider />
                <v-toolbar
                    class="mobile-ticket-action-bar"
                >
                    <v-spacer />
                    <v-btn
                        class="ma-1 action-btn text-subtitle-2"
                        depressed
                        width="80%"
                        color="primary"
                        :disabled="respondDisabled"
                        @click="respond"
                    >
                        <v-icon
                            left
                            class="action-icon"
                        >
                            reply
                        </v-icon>
                        Respond
                    </v-btn>
                    <v-spacer />
                </v-toolbar>                
            </v-col>
        </v-row>
        <TicketsResponseDialog
            :selected-tickets="selected"
            :show="showTicketsResponseDialog"
            @close-response-dialog="closeRespond"
            @reponse-success="responseSuccess"
            @offline-reponse-success="responseSuccess"
        />
        <OfflineResponseToast
            :show-response-toast="showResponseToast"
            @close-offline-response-toast="showResponseToast = false" 
        />
    </v-container>
</template>
<script>
import ClipboardItemPositionControls from '../ClipboardItemPositionControls';
import TicketsMobileCard from '../TicketsMobileCard';
import TicketsResponseDialog from '../TicketsResponseDialog.vue';
import OfflineResponseToast from './OfflineResponseToast';
import { statusText, isSType, fieldHeaderMap, formatTicketDate, defaultOpenTicketsTabOptions } from '@/services/ticketDataHelpers';
import { getTicketTypeColorDefaults } from '@/utils/ticketTypeColors';
import { getUserOption } from '@/utils/options';
import { guidEqual, guidNormal } from '@/utils/guid.js';
import { riskScoreToText } from '@/utils/ticketRisk.js';
import {validateRespondSelection} from '@/services/ticketsResponse';

export default {
    components: {
        ClipboardItemPositionControls,
        TicketsMobileCard,
        TicketsResponseDialog,
        OfflineResponseToast,
    },
    data: () => ({
        headers: [
            {
                text: 'test',
                value: 'clipboard',
                width: '30px',
                sortable: false,
            },
            {
                text: 'Due',
                value: 'dueDateText',
                width: '100px',
            },
            {
                text: 'Ticket',
                value: 'ticketRevision',
                width: '156px'
            },
            {
                text: 'Status',
                value: 'TR_stage',
                align: 'left',
                width: '80px'
            },
        ],
        itemsPerPage: 100,
        selected: [],
        clipboardAll: [],
        showTicketsResponseDialog: false,
        showResponseToast: false,
    }),
    computed: {
        tickets: {
            get() {
                return this.$store.getters.userClipboard.map(ticket => {
                    this.formatTicketForTable(ticket);
                    return ticket;
                });
            },
            set(newValue) {
                this.$store.commit('SET_USER_CLIPBOARD', newValue);
            }
        },
       
        userConfigColors() {
            let usercolors = getUserOption('JSON:UI-COLORS');
            
            if(!usercolors) {
                usercolors = getTicketTypeColorDefaults();
            }
            return usercolors;
        },
        reopenDisabled(){
            return this.selected.length === 0;
        },
        respondDisabled(){
            return this.selected.length === 0;
        },
        mobileSelectAll: {
            set(newValue) {
                this.selected = newValue ? [...this.tickets] : [];
            },
            get() {
                const ticketCount = this.tickets.length;
                return ticketCount > 0 && ticketCount === this.selected.length;
            },
        },
        companyName() {
            return this.$store.getters.company.company;
        },
    },
    mounted() {
        this.loadTicketTableHeader();
    },
    methods: {
        guidNormal,
        updateClipboardItems(newList) {
            this.tickets = newList;
        },
        statusChipClass(stage) {
            return `status-${statusText(stage).toLowerCase()}-chip`;
        },
        statusText(stage) {
            return statusText(stage);
        },
        priorityIconColor(priority) {
            switch(priority) {
                case 'NORM':
                    return 'grey';
                case 'RUSH':
                    return '#fdd835';
                case 'EMER':
                    return 'red';
                case 'NONC':
                    return 'light-blue';
                default:
                    return '';
            }
        },
        priorityIcon(priority) {
            switch(priority) {
                case 'NORM':
                    // gray circle exclamation
                    return 'error';
                case 'RUSH':
                    // yellow circle exclamation
                    return 'error';
                case 'EMER':
                    // red bell exclamation
                    return 'notification_important';
                case 'NONC':
                    // light blue circle exclamation
                    return 'error';
                default:
                    return '';
            }
        },        
        itemRowColor(ticket) {
            for(let colorCode of this.userConfigColors) {
                if(isSType(colorCode.type, ticket)) {
                    return colorCode.class;
                }
            }
        },
        setClipboardCheckState() {
            return true;
        },
        setMobileCheckBoxState(a, b) {
            return guidEqual(a?.TR_rowid, b?.TR_rowid);
        },
        statusChipColor(stage) {
            return stage === 'CT' ? '#FFB300' : 'green';
        },
        formatTicketForTable(ticket) {
            ticket.dueDateText = this.dueDateText(ticket.duedate);
            ticket.ticketRevision = ticket.ticket;
            if(ticket.revision) {
                ticket.ticketRevision += '-' + ticket.revision;
            }
            return ticket;
        },    
        dueDateText(duedate) {
            const now = new Date();
            if (duedate) {
                const d = Date.parse(duedate);
                if (d <= now) {
                    return 'Overdue';
                }
                const diff = d - now;
                const hr = 1 + Math.floor(diff / 3600000);
                return '<' + hr + 'Hr';
            }
            return '?UNKNOWN';
        },
        getClipboardConfig(){
            const tabJson = getUserOption('JSON:TAB:tickets-clipboard');
            return tabJson ? JSON.parse(tabJson) : defaultOpenTicketsTabOptions;
        },
        loadTicketTableHeader(){
            let fields = this.getClipboardConfig.config?.fields;
            if(!fields?.length) {
                fields = 'center,TR_member_code,type,TR_seq_date,TR_seq_num,TR_locator,county,place,company,work_type';
            }
            fields = fields.split(',');
            let newHeaders = [];
            const staticFields = [
                'ticket',
                'revision',
            ];
            newHeaders.push(Object.assign({},this.headers[0]));  // clipboard column              
            newHeaders.push(Object.assign({},this.headers[1]));  // due column
            newHeaders.push(Object.assign({},this.headers[2]));  // ticket column
            newHeaders.push(Object.assign({},this.headers[3]));  // Status column
            for(let field of fields) {
                if(staticFields.includes(field)) {
                    continue;
                }
                const header = fieldHeaderMap[field];

                if(!header) {
                    console.error('syncTicketTableHeader could not sync field ', field);
                    continue;
                }
                newHeaders.push({
                    text: header.text,
                    value: field,
                    align: 'left',
                    sortable: true,
                    width: header.width
                });
            }
            this.headers = newHeaders;
        },
        riskScoreText(risk) {
            return riskScoreToText(risk);
        },
        formatTicketDate(dateString) {
            return formatTicketDate(dateString);
        },
        respond() {
            let errorText = validateRespondSelection(
                this.selected,
                this.$store.getters.companyResponseCodes,
                isSType);
            if(errorText) {
                this.validateResponseDialogText = errorText;
                this.validateResponseDialog = true;
            } else {
                this.showTicketsResponseDialog = true;
            }
        },
        closeRespond() {
            this.showTicketsResponseDialog = false;
        },
        responseSuccess() {
            // May need to call this
            // this.updateTickets(result);
            this.closeRespond();
            this.showResponseToast = true;
        },
        dialogBtnClick() {

        },

    }
};
</script>
<style>
#offline-clipboard-table.v-data-table {
    border: 1px solid #ccc;
    width: 100%;
    max-height:calc(100vh - 200px);
    overflow: auto;
}
#offline-clipboard-table.v-data-table__wrapper {
    border-radius: 4px;
}
#offline-clipboard-table table tr:last-child td {
    border-bottom: 1px solid #ccc;
}
#offline-clipboard-table table tr td:nth-child(1), 
#offline-clipboard-table table tr th:nth-child(1),
#offline-clipboard-table table tr td:nth-child(2), 
#offline-clipboard-table table tr th:nth-child(2),
#offline-clipboard-table table tr td:nth-child(3), 
#offline-clipboard-table table tr th:nth-child(3),
#offline-clipboard-table table tr td:nth-child(4), 
#offline-clipboard-table table tr th:nth-child(4),
#offline-clipboard-table table tr td:nth-child(5), 
#offline-clipboard-table table tr th:nth-child(5) {
    position: sticky !important; 
    position: -webkit-sticky !important; 
    z-index: 2;
}
#offline-clipboard-table table tr td:nth-child(1), 
#offline-clipboard-table table tr th:nth-child(1) {
    left: 0;
    border-radius:0;
}
#offline-clipboard-table table tr td:nth-child(2), 
#offline-clipboard-table table tr th:nth-child(2) {
    left: 64px;
}
#offline-clipboard-table table tr td:nth-child(3), 
#offline-clipboard-table table tr th:nth-child(3) {
    left: calc(64px + 64px);
}
#offline-clipboard-table table tr td:nth-child(4), 
#offline-clipboard-table table tr th:nth-child(4) {
    left: calc(64px + 64px + 100px);
}
#offline-clipboard-table table tr td:nth-child(5), 
#offline-clipboard-table table tr th:nth-child(5) {
    left: calc(64px + 64px + 100px + 156px);
    border-right:3px solid rgba( 0, 0, 0, 0.04 );
}
#offline-clipboard-table table tr th:nth-child(1),
#offline-clipboard-table table tr th:nth-child(2),
#offline-clipboard-table table tr th:nth-child(3),
#offline-clipboard-table table tr th:nth-child(4),
#offline-clipboard-table table tr th:nth-child(5) {
    z-index: 3!important;
}
.status-open-chip.v-chip.v-chip--outlined.v-chip.v-chip{
    background-color: rgba(73, 200, 79, .1) !important;
    padding: 0 6px;
}
.status-closed-chip.v-chip.v-chip--outlined.v-chip.v-chip{
    background-color: rgba(255, 179, 0, .1) !important;
    padding: 0 6px;
}
#offline-clipboard-table thead,
#offline-clipboard-table th {
    background-color: #F5F7FA;
}
#offline-clipboard-table td {
    background:  #fff;
}
#offline-clipboard-table .v-data-table__selected td {
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #F26227;
}

.title {
  font-size: 2rem !important;
}
.ticket-chip {
  border-radius: 20px !important;
  height: 18px !important;
  padding: 0px 4px !important;
}
</style>